import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Eye, Edit, Trash2, RefreshCw } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { useDialog } from '../components/DialogProvider';

const HomeContentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getHomeContents, getTestQuestions, deleteHomeContent, syncTestCounts } = useFirebase();
  const dialog = useDialog();
  
  const [content, setContent] = useState(null);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (id) {
      loadContent();
    }
  }, [id]);

  const loadContent = async () => {
    try {
      setLoading(true);
      
      // Load content details
      const contents = await getHomeContents();
      const foundContent = contents.find(c => c.id === id);
      
      if (foundContent) {
        setContent(foundContent);
        
        // Load tests for this content
        const testQuestions = await getTestQuestions(id);
        
        if (testQuestions && testQuestions.length > 0) {
          // Group questions by test title
          const testsMap = testQuestions.reduce((acc, question) => {
            const testTitle = question.testName || question.title || question.testTitle || 'Untitled Test';
            if (!acc[testTitle]) {
              acc[testTitle] = {
                id: question.id || `test_${Date.now()}_${Math.random()}`,
                title: testTitle,
                testName: testTitle,
                startDate: question.startDate,
                endDate: question.endDate,
                questions: question.questions || [],
                totalQuestions: question.totalQuestions || 0,
                createdAt: question.createdAt || new Date().toISOString(),
                status: question.status || 'draft'
              };
            }
            if (question.questions && question.questions.length > 0) {
              acc[testTitle].questions = question.questions;
            }
            return acc;
          }, {});
          
          setTests(Object.values(testsMap));
        }
      } else {
        toast.error('Test content not found');
        navigate('/home');
      }
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Error loading test content');
      navigate('/home');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const ok = await dialog.confirm({
      title: 'Delete Test Content',
      message: `Are you sure you want to delete "${content.title}"? This action cannot be undone.`,
      confirmText: 'Yes, Delete',
      tone: 'danger'
    })
    if (!ok) return;
    try {
      setLoading(true);
      await deleteHomeContent(id);
      toast.success('Test content deleted successfully!');
      navigate('/home');
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Error deleting test content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncTestCounts = async () => {
    try {
      setLoading(true);
      const newCount = await syncTestCounts(id);
      toast.success(`Test count synced successfully! Updated to ${newCount} tests.`);
      await loadContent(); // Reload to show updated count
    } catch (error) {
      console.error('Error syncing test counts:', error);
      toast.error('Error syncing test counts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Test content not found</h2>
          <button
            onClick={() => navigate('/home')}
            className="btn-primary"
          >
            Back to Tests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/home')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold">View Test Content</h1>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/home-content/edit/${id}`)}
            className="btn-secondary flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Content Details */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Test Content Image
            </label>
            <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
              {content.imageUrl ? (
                <img
                  src={content.imageUrl}
                  alt={content.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileText className="w-16 h-16 text-gray-400" />
              )}
            </div>
          </div>

          {/* Details */}
          <div className="md:col-span-2">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <p className="text-lg font-semibold text-gray-900">{content.title}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tests Count
                  </label>
                  <p className="text-2xl font-bold text-blue-600">{tests.length}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Created Date
                </label>
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{content.createdAt ? new Date(content.createdAt).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tests Section */}
      <div className="card">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Tests ({tests.length})</h2>
            <div className="flex gap-2">
              <button
                onClick={handleSyncTestCounts}
                className="btn-secondary flex items-center gap-2"
                title="Sync test count with Firestore"
              >
                <RefreshCw className="w-4 h-4" />
                Sync Count
              </button>
              <button
                onClick={() => navigate(`/tests-management/${id}`)}
                className="btn-primary"
              >
                Manage Tests
              </button>
            </div>
          </div>
        </div>

        {tests.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {tests.map((test) => (
              <div key={test.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{test.title}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {test.questions.length} questions
                      </span>
                      <span>
                        Created: {new Date(test.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/tests-management/${id}`)}
                    className="btn-secondary"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tests created yet</h3>
            <p className="text-gray-600 mb-6">Start by creating your first test for this content.</p>
            <button
              onClick={() => navigate(`/tests-management/${id}`)}
              className="btn-primary"
            >
              Create First Test
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeContentView;